/**
 * Invitation Status and Type Badge Components
 *
 * Reusable badge components for displaying invitation status and delivery type.
 * Follows the RecipientStatusBadge pattern from CampaignDetailModal.
 *
 * Used by:
 * - InvitationTrackingTable (shared component)
 * - USI Events Invitation Tracking page
 * - HMC Events Invitation Tracking page
 */

import React from 'react';
import { Archive, FileText, Send } from 'lucide-react';
import type { InvitationStatus, InvitationType } from '@shared/types/invitationTracking';

// ============================================================
// STATUS BADGE
// ============================================================

/**
 * Color and label configuration for each invitation status.
 * Matches the dark theme palette used across the application.
 */
const statusConfig: Record<InvitationStatus, { color: string; label: string }> = {
  pending: { color: 'bg-gray-500/20 text-dark-muted', label: 'Pending' },
  sending: { color: 'bg-yellow-500/20 text-yellow-400', label: 'Sending' },
  sent: { color: 'bg-blue-500/20 text-blue-400', label: 'Sent' },
  delivered: { color: 'bg-green-500/20 text-green-400', label: 'Delivered' },
  opened: { color: 'bg-emerald-500/20 text-emerald-400', label: 'Opened' },
  clicked: { color: 'bg-teal-500/20 text-teal-400', label: 'Clicked' },
  failed: { color: 'bg-red-500/20 text-red-400', label: 'Failed' },
  bounced: { color: 'bg-red-500/20 text-red-400', label: 'Bounced' },
};

interface InvitationStatusBadgeProps {
  status: InvitationStatus;
}

/**
 * Status badge for invitation delivery status.
 * Displays a colored pill with the status label.
 */
export function InvitationStatusBadge({ status }: InvitationStatusBadgeProps) {
  const { color, label } = statusConfig[status];

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      {label}
    </span>
  );
}

// ============================================================
// TYPE BADGE
// ============================================================

/**
 * Color, label, and icon configuration for each invitation type.
 */
const typeConfig: Record<InvitationType, { color: string; label: string; icon: React.FC<{ className?: string }> }> = {
  zip: { color: 'bg-purple-500/20 text-purple-400', label: 'ZIP Download', icon: Archive },
  eml: { color: 'bg-blue-500/20 text-blue-400', label: 'EML File', icon: FileText },
  resend: { color: 'bg-brand-500/20 text-brand-400', label: 'Email (Resend)', icon: Send },
};

interface InvitationTypeBadgeProps {
  type: InvitationType;
}

/**
 * Type badge for invitation delivery method.
 * Displays a colored pill with an icon and type label.
 */
export function InvitationTypeBadge({ type }: InvitationTypeBadgeProps) {
  const { color, label, icon: Icon } = typeConfig[type];

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

// ============================================================
// EXPORTS & UTILITIES
// ============================================================

/**
 * All possible invitation statuses, useful for building filter UI.
 */
export const ALL_STATUSES: InvitationStatus[] = [
  'pending',
  'sending',
  'sent',
  'delivered',
  'opened',
  'clicked',
  'failed',
  'bounced',
];

/**
 * All possible invitation types, useful for building filter UI.
 */
export const ALL_TYPES: InvitationType[] = ['zip', 'eml', 'resend'];

/**
 * Get the display label for a status.
 */
export function getStatusLabel(status: InvitationStatus): string {
  return statusConfig[status].label;
}

/**
 * Get the display label for a type.
 */
export function getTypeLabel(type: InvitationType): string {
  return typeConfig[type].label;
}
